import { useEffect, useState, useCallback, useRef } from "react";

/**
 * usePwaInstall — hook central de instalação PWA.
 *
 * Captura o evento ``beforeinstallprompt`` ANTES que o browser exiba o
 * banner nativo (chamamos ``e.preventDefault()`` imediato), o segura na
 * ref e expõe ``promptInstall()`` para que o nosso Smart Banner decida
 * QUANDO disparar (após 30 % de scroll OU 10 s — controle no consumer).
 *
 * Também detecta:
 *   - ``platform``: "android" | "ios" | "desktop" | "other"
 *   - ``isStandalone``: já está rodando como app instalado?
 *   - ``isInstallable``: o browser permitiu install prompt (Android-only)
 *
 * Eventos GA4 (gtag, se disponível):
 *   - ``pwa_install_prompt_shown``  — quando chamamos promptInstall()
 *   - ``pwa_install_accepted``      — usuário aceitou
 *   - ``pwa_install_dismissed``     — usuário dispensou
 *   - ``pwa_installed``             — evento global ``appinstalled``
 */
function detectPlatform() {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return "ios";
  if (/Android/.test(ua)) return "android";
  return "desktop";
}

function isStandaloneMode() {
  if (typeof window === "undefined") return false;
  // iOS Safari usa navigator.standalone; Android/desktop usam matchMedia.
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

function ga4(name, params = {}) {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", name, params);
    }
  } catch (_) { /* noop */ }
}

/** Obtém (ou cria) um identificador opaco por dispositivo para
 * deduplicar instalações dentro do backend. NÃO contém PII. */
function getOrCreateClientId() {
  try {
    let id = localStorage.getItem("jt_pwa_client_id");
    if (!id) {
      // crypto.randomUUID é disponível em todos os browsers modernos;
      // fallback para um id pseudo-único caso indisponível.
      id = (window.crypto?.randomUUID?.() ||
            `c_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);
      localStorage.setItem("jt_pwa_client_id", id);
    }
    return id;
  } catch { return `anon_${Date.now()}`; }
}

/** Envia ping idempotente ao backend quando o PWA é instalado. */
async function pingInstall(platform) {
  try {
    const apiBase = process.env.REACT_APP_BACKEND_URL || "";
    await fetch(`${apiBase}/api/pwa/install-ping`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        client_id: getOrCreateClientId(),
        platform,
        referrer: document.referrer || "",
      }),
      keepalive: true,
    });
  } catch { /* ping silencioso */ }
}

export default function usePwaInstall() {
  const [platform, setPlatform] = useState("other");
  const [isStandalone, setIsStandalone] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);
  const deferredRef = useRef(null);

  useEffect(() => {
    setPlatform(detectPlatform());
    setIsStandalone(isStandaloneMode());

    const onBeforeInstall = (e) => {
      e.preventDefault();           // segura o prompt nativo
      deferredRef.current = e;
      setIsInstallable(true);
    };
    const onInstalled = () => {
      setIsInstallable(false);
      deferredRef.current = null;
      const plat = detectPlatform();
      ga4("pwa_installed", { platform: plat });
      pingInstall(plat);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // iOS NÃO dispara ``appinstalled``. Detectamos a instalação
    // observando a PRIMEIRA vez que o site abre em ``display-mode:
    // standalone``: gravamos um flag no localStorage para não pingar
    // de novo. Em Android o ping já veio do ``appinstalled``; este
    // ramo cobre iPhone/iPad e qualquer browser que esqueça o evento.
    if (isStandaloneMode()) {
      try {
        if (!localStorage.getItem("jt_pwa_standalone_seen")) {
          localStorage.setItem("jt_pwa_standalone_seen", String(Date.now()));
          const plat = detectPlatform();
          ga4("pwa_first_standalone", { platform: plat });
          pingInstall(plat);
        }
      } catch { /* noop */ }
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    const ev = deferredRef.current;
    if (!ev) return { outcome: "unavailable" };
    ga4("pwa_install_prompt_shown", { platform });
    try {
      ev.prompt();
      const choice = await ev.userChoice;
      if (choice?.outcome === "accepted") {
        ga4("pwa_install_accepted", { platform });
      } else {
        ga4("pwa_install_dismissed", { platform });
      }
      deferredRef.current = null;
      setIsInstallable(false);
      return choice || { outcome: "dismissed" };
    } catch (e) {
      return { outcome: "error" };
    }
  }, [platform]);

  return { platform, isStandalone, isInstallable, promptInstall };
}
